import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Save, ArrowLeft, AlertTriangle, Info, Calculator } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayrollFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useLanguage();
  const isEdit = Boolean(id);
  
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [carryOver, setCarryOver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    employee_id: '',
    period_start: '',
    period_end: '',
    regular_hours: 0,
    overtime_hours: 0,
    night_hours: 0,
    holiday_hours: 0,
    weekend_hours: 0,
    vacation_days: 0,
    sick_days: 0,
    one_time_bonus: 0,
    commission: 0,
    special_payments: 0,
    advance_deduction: 0,
    loan_deduction: 0,
    notes: ''
  });

  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${API}/employees`, { params: { is_active: true } });
        setEmployees(response.data);
      } catch (error) {
        console.error('Error fetching employees:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchPayroll = async () => {
      try {
        const response = await axios.get(`${API}/payroll/${id}`);
        const payroll = response.data;
        
        if (payroll.status === 'finalized') {
          toast.error(t('cannotEditFinalized'));
          navigate('/payroll');
          return;
        }
        
        setForm({
          employee_id: payroll.employee_id,
          period_start: payroll.period_start,
          period_end: payroll.period_end,
          regular_hours: payroll.regular_hours || 0,
          overtime_hours: payroll.overtime_hours || 0,
          night_hours: payroll.night_hours || 0,
          holiday_hours: payroll.holiday_hours || 0,
          weekend_hours: payroll.weekend_hours || 0,
          vacation_days: payroll.vacation_days || 0,
          sick_days: payroll.sick_days || 0,
          one_time_bonus: payroll.one_time_bonus || 0,
          commission: payroll.commission || 0,
          special_payments: payroll.special_payments || 0,
          advance_deduction: payroll.advance_deduction || 0,
          loan_deduction: payroll.loan_deduction || 0,
          notes: payroll.notes || ''
        });
        
        const emp = employees.find(e => e.id === payroll.employee_id);
        setSelectedEmployee(emp);
      } catch (error) {
        console.error('Error fetching payroll:', error);
        toast.error(t('loadingError'));
        navigate('/payroll');
      }
    };

    if (isEdit && employees.length > 0) {
      fetchPayroll();
    }
  }, [isEdit, employees, id, navigate, t]);

  useEffect(() => {
    const fetchCarryOver = async (empId) => {
      try {
        const response = await axios.get(`${API}/carry-over/${empId}`);
        setCarryOver(response.data);
        
        // Auto-fill suggested deductions only if there are open amounts
        if (response.data.total_open_advances > 0 || response.data.suggested_loan_payment > 0) {
          setForm(prev => ({
            ...prev,
            advance_deduction: response.data.total_open_advances || 0,
            loan_deduction: response.data.suggested_loan_payment || 0
          }));
        }
      } catch (error) {
        console.error('Error fetching carry-over:', error);
      }
    };

    if (form.employee_id && !isEdit) {
      fetchCarryOver(form.employee_id);
      const emp = employees.find(e => e.id === form.employee_id);
      setSelectedEmployee(emp);
    }
  }, [form.employee_id, employees, isEdit]);

  const applyAdvanceDeduction = () => {
    if (carryOver) {
      setForm(prev => ({
        ...prev,
        advance_deduction: carryOver.total_open_advances
      }));
    }
  };

  const applyLoanDeduction = () => {
    if (carryOver) {
      setForm(prev => ({
        ...prev,
        loan_deduction: carryOver.suggested_loan_payment
      }));
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Calculate preview whenever form or selectedEmployee changes
  useEffect(() => {
    if (!selectedEmployee) return;
    
    const hourlyRate = selectedEmployee.hourly_rate || 0;
    const dailyHours = selectedEmployee.standard_daily_hours || 8;
    const contractType = selectedEmployee.contract_type || 'vollzeit';
    
    // Sick days calculation - 8h for full-time, 4h for part-time
    const sickDays = parseInt(form.sick_days) || 0;
    const sickHoursPerDay = (contractType === 'vollzeit' || contractType === 'befristet') ? 8 : 4;
    const sickHours = sickDays * sickHoursPerDay;
    const sickPay = sickHours * hourlyRate;
    
    const vacationHours = (parseInt(form.vacation_days) || 0) * dailyHours;
    
    // Base gross calculation
    let baseGross;
    if (selectedEmployee.monthly_fixed_salary) {
      baseGross = selectedEmployee.monthly_fixed_salary;
    } else {
      baseGross = (parseFloat(form.regular_hours) || 0) * hourlyRate;
    }
    
    // Overtime calculation with configurable percentage
    const overtimePct = selectedEmployee.overtime_surcharge_percent || 25;
    const overtimeHours = parseFloat(form.overtime_hours) || 0;
    const overtimePay = overtimeHours * hourlyRate * (1 + overtimePct / 100);
    
    // Night hours calculation
    const nightPct = selectedEmployee.night_surcharge_percent || 0;
    const nightHours = parseFloat(form.night_hours) || 0;
    const nightSurcharge = nightHours * hourlyRate * (1 + nightPct / 100);
    
    // Holiday surcharge
    const holidayPct = selectedEmployee.holiday_surcharge_percent || 0;
    const holidayHours = parseFloat(form.holiday_hours) || 0;
    const holidaySurcharge = holidayHours * hourlyRate * (1 + holidayPct / 100);
    
    // Weekend surcharge
    const weekendPct = selectedEmployee.weekend_surcharge_percent || 0;
    const weekendHours = parseFloat(form.weekend_hours) || 0;
    const weekendSurcharge = weekendHours * hourlyRate * (1 + weekendPct / 100);
    
    // Other surcharges
    const otherPct = selectedEmployee.other_surcharge_percent || 0;
    const otherSurcharges = ((parseFloat(form.regular_hours) || 0) + overtimeHours) * hourlyRate * (otherPct / 100);
    
    const totalSurcharges = holidaySurcharge + weekendSurcharge + otherSurcharges + nightSurcharge;
    
    // Bonus calculation
    const standardBonus = selectedEmployee.standard_bonus || 0;
    const oneTimeBonus = parseFloat(form.one_time_bonus) || 0;
    const commission = parseFloat(form.commission) || 0;
    const specialPayments = parseFloat(form.special_payments) || 0;
    const totalBonus = standardBonus + oneTimeBonus + commission + specialPayments;
    
    // Vacation pay
    const vacationPay = vacationHours * hourlyRate;
    
    // Deductions (shown as info only - will be deducted from NET by tax advisor)
    const advanceDeduction = parseFloat(form.advance_deduction) || 0;
    const loanDeduction = parseFloat(form.loan_deduction) || 0;
    const totalDeductions = advanceDeduction + loanDeduction;
    
    // Totals (including sick pay)
    const totalGross = baseGross + overtimePay + totalSurcharges + totalBonus + vacationPay + sickPay;
    // Payout gross = total gross (deductions are taken from NET, not GROSS)
    const payoutGross = totalGross;
    
    setPreview({
      hourlyRate,
      baseGross: baseGross.toFixed(3),
      // Overtime
      overtimeHours,
      overtimePct,
      overtimePay: overtimePay.toFixed(3),
      // Night
      nightHours,
      nightPct,
      nightSurcharge: nightSurcharge.toFixed(3),
      // Holiday
      holidayHours,
      holidayPct,
      holidaySurcharge: holidaySurcharge.toFixed(3),
      // Weekend
      weekendHours,
      weekendPct,
      weekendSurcharge: weekendSurcharge.toFixed(3),
      // Other
      otherPct,
      otherSurcharges: otherSurcharges.toFixed(3),
      // Total surcharges
      totalSurcharges: totalSurcharges.toFixed(3),
      // Bonus breakdown
      standardBonus: standardBonus.toFixed(3),
      oneTimeBonus: oneTimeBonus.toFixed(3),
      commission: commission.toFixed(3),
      specialPayments: specialPayments.toFixed(3),
      totalBonus: totalBonus.toFixed(3),
      // Vacation
      vacationHours,
      vacationPay: vacationPay.toFixed(3),
      // Sick days
      sickDays,
      sickHours,
      sickHoursPerDay,
      sickPay: sickPay.toFixed(3),
      // Deductions
      advanceDeduction: advanceDeduction.toFixed(3),
      loanDeduction: loanDeduction.toFixed(3),
      totalDeductions: totalDeductions.toFixed(3),
      // Totals
      totalGross: totalGross.toFixed(3),
      payoutGross: payoutGross.toFixed(3)
    });
  }, [form, selectedEmployee]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...form,
        regular_hours: parseFloat(form.regular_hours) || 0,
        overtime_hours: parseFloat(form.overtime_hours) || 0,
        night_hours: parseFloat(form.night_hours) || 0,
        holiday_hours: parseFloat(form.holiday_hours) || 0,
        weekend_hours: parseFloat(form.weekend_hours) || 0,
        vacation_days: parseInt(form.vacation_days) || 0,
        sick_days: parseInt(form.sick_days) || 0,
        one_time_bonus: parseFloat(form.one_time_bonus) || 0,
        commission: parseFloat(form.commission) || 0,
        special_payments: parseFloat(form.special_payments) || 0,
        advance_deduction: parseFloat(form.advance_deduction) || 0,
        loan_deduction: parseFloat(form.loan_deduction) || 0
      };

      if (isEdit) {
        await axios.put(`${API}/payroll/${id}`, data);
        toast.success(t('updatedSuccessfully'));
      } else {
        await axios.post(`${API}/payroll`, data);
        toast.success(t('savedSuccessfully'));
      }
      navigate('/payroll');
    } catch (error) {
      console.error('Error saving payroll:', error);
      // Handle Pydantic validation errors (array of objects)
      const detail = error.response?.data?.detail;
      let errorMessage = t('errorOccurred');
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || JSON.stringify(detail);
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value || 0) + ' TND';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payroll-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/payroll')}
          className="text-gray-500 hover:text-[#1A1A1A]"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-3xl font-bold text-[#1A1A1A]">
          {isEdit ? t('editPayroll') : t('newPayroll')}
        </h1>
        {isEdit && (
          <span className="text-sm bg-amber-100 text-amber-700 px-3 py-1 rounded-full">
            {t('draft')}
          </span>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Employee & Period */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-[#1A1A1A]">{t('payrollPeriod')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('employees')} *</Label>
                    <Select 
                      value={form.employee_id} 
                      onValueChange={(v) => handleChange('employee_id', v)}
                      disabled={isEdit}
                    >
                      <SelectTrigger 
                        className={`bg-white border-gray-200 text-[#1A1A1A] ${isEdit ? 'opacity-60 cursor-not-allowed' : ''}`} 
                        data-testid="select-employee"
                      >
                        <SelectValue placeholder={t('employees')} />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.employee_number} - {emp.first_name} {emp.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isEdit && (
                      <p className="text-xs text-gray-400">{t('employeeCannotBeChanged')}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('periodStart')} *</Label>
                    <Input
                      type="date"
                      value={form.period_start}
                      onChange={(e) => handleChange('period_start', e.target.value)}
                      required
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-period-start"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('periodEnd')} *</Label>
                    <Input
                      type="date"
                      value={form.period_end}
                      onChange={(e) => handleChange('period_end', e.target.value)}
                      required
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-period-end"
                    />
                  </div>
                </div>
                
                {/* Employee Info Display */}
                {selectedEmployee && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">{t('employeeId')}:</span>
                        <span className="ml-2 font-medium text-[#1A1A1A]">{selectedEmployee.employee_number}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('contractType')}:</span>
                        <span className="ml-2 font-medium text-[#1A1A1A]">{t(selectedEmployee.contract_type) || selectedEmployee.contract_type}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('hourlyRate')}:</span>
                        <span className="ml-2 font-medium text-[#1A1A1A]">{selectedEmployee.hourly_rate?.toFixed(3)} TND</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('position')}:</span>
                        <span className="ml-2 font-medium text-[#1A1A1A]">{selectedEmployee.position || '-'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Hours */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-[#1A1A1A]">Arbeitsstunden</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('regularHours')}</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={form.regular_hours}
                      onChange={(e) => handleChange('regular_hours', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-regular-hours"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('overtimeHours')}</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={form.overtime_hours}
                      onChange={(e) => handleChange('overtime_hours', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-overtime-hours"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('nightHours')}</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={form.night_hours}
                      onChange={(e) => handleChange('night_hours', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-night-hours"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('holidayHours')}</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={form.holiday_hours}
                      onChange={(e) => handleChange('holiday_hours', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-holiday-hours"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('weekendHours')}</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={form.weekend_hours}
                      onChange={(e) => handleChange('weekend_hours', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-weekend-hours"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-500">{t('vacationDays')}</Label>
                      {selectedEmployee && (
                        <span className={`text-xs ${(selectedEmployee.vacation_days_remaining || 0) <= 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                          {t('available')}: {selectedEmployee.vacation_days_remaining || 0} {t('days')}
                        </span>
                      )}
                    </div>
                    <Input
                      type="number"
                      min="0"
                      max={selectedEmployee?.vacation_days_remaining || 0}
                      value={form.vacation_days}
                      onChange={(e) => {
                        const maxDays = selectedEmployee?.vacation_days_remaining || 0;
                        const value = Math.min(Math.max(0, parseInt(e.target.value) || 0), maxDays);
                        handleChange('vacation_days', value);
                      }}
                      disabled={!selectedEmployee || (selectedEmployee.vacation_days_remaining || 0) <= 0}
                      className={`bg-white border-gray-200 text-[#1A1A1A] ${
                        !selectedEmployee || (selectedEmployee.vacation_days_remaining || 0) <= 0 
                          ? 'opacity-50 cursor-not-allowed' 
                          : ''
                      }`}
                      data-testid="input-vacation-days"
                    />
                    {selectedEmployee && (selectedEmployee.vacation_days_remaining || 0) <= 0 && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {t('noVacationDaysRemaining')}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-500">{t('sickDays')}</Label>
                      {selectedEmployee && (
                        <span className="text-xs text-gray-400">
                          {selectedEmployee.contract_type === 'vollzeit' || selectedEmployee.contract_type === 'befristet' 
                            ? '8h / Tag' 
                            : '4h / Tag'}
                        </span>
                      )}
                    </div>
                    <Input
                      type="number"
                      min="0"
                      value={form.sick_days}
                      onChange={(e) => handleChange('sick_days', Math.max(0, parseInt(e.target.value) || 0))}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-sick-days"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bonus & Extras */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-[#1A1A1A]">Bonus & Extras</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('oneTimeBonus')}</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.one_time_bonus}
                      onChange={(e) => handleChange('one_time_bonus', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-one-time-bonus"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('commission')}</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.commission}
                      onChange={(e) => handleChange('commission', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-commission"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('specialPayments')}</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.special_payments}
                      onChange={(e) => handleChange('special_payments', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-special-payments"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Deductions */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-[#1A1A1A]">Abzüge / Déductions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-500">{t('advanceDeduction')}</Label>
                      {carryOver && carryOver.total_open_advances > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={applyAdvanceDeduction}
                          className="text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-6 px-2"
                          data-testid="apply-advance-btn"
                        >
                          Offen: {formatCurrency(carryOver.total_open_advances)} übernehmen
                        </Button>
                      )}
                    </div>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.advance_deduction}
                      onChange={(e) => handleChange('advance_deduction', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-advance-deduction"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-500">{t('loanDeduction')}</Label>
                      {carryOver && carryOver.suggested_loan_payment > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={applyLoanDeduction}
                          className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-6 px-2"
                          data-testid="apply-loan-btn"
                        >
                          {t('suggestedRate')}: {formatCurrency(carryOver.suggested_loan_payment)}
                        </Button>
                      )}
                    </div>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.loan_deduction}
                      onChange={(e) => handleChange('loan_deduction', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-loan-deduction"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Carry Over Info */}
            {carryOver && (carryOver.total_open_advances > 0 || carryOver.total_loan_balance > 0) && (
              <Card className="bg-white border-gray-200 border-l-4 border-l-[#E31E24]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-[#1A1A1A] text-lg flex items-center gap-2">
                    <Info className="w-5 h-5 text-[#E31E24]" />
                    {t('carryOverTitle')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Offene Vorschüsse */}
                  {carryOver.total_open_advances > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        <span className="font-semibold text-amber-800">{t('openAdvancesTitle')}</span>
                      </div>
                      <div className="space-y-1">
                        {carryOver.open_advances && carryOver.open_advances.map((adv, idx) => (
                          <div key={idx} className="flex justify-between text-sm text-amber-700">
                            <span>{adv.date}</span>
                            <span className="font-mono">{formatCurrency(adv.remaining)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm font-bold text-amber-800 pt-1 border-t border-amber-200">
                          <span>{t('totalOpenAmount')}</span>
                          <span className="font-mono">{formatCurrency(carryOver.total_open_advances)}</span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={applyAdvanceDeduction}
                        className="w-full mt-2 text-amber-700 border-amber-300 hover:bg-amber-100"
                        data-testid="sidebar-apply-advance-btn"
                      >
                        {t('applyTotalAmount')}
                      </Button>
                    </div>
                  )}
                  
                  {/* Aktive Darlehen */}
                  {carryOver.total_loan_balance > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Info className="w-4 h-4 text-blue-600" />
                        <span className="font-semibold text-blue-800">{t('activeLoansTitle')}</span>
                      </div>
                      <div className="space-y-1">
                        {carryOver.active_loans && carryOver.active_loans.map((loan, idx) => (
                          <div key={idx} className="text-sm text-blue-700">
                            <div className="flex justify-between">
                              <span>{t('remainingBalance')}</span>
                              <span className="font-mono">{formatCurrency(loan.remaining)}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span>{t('monthlyRate')}</span>
                              <span className="font-mono">{formatCurrency(loan.monthly_rate)}</span>
                            </div>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm font-bold text-blue-800 pt-1 border-t border-blue-200">
                          <span>{t('totalDebt')}</span>
                          <span className="font-mono">{formatCurrency(carryOver.total_loan_balance)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-blue-700">
                          <span>{t('suggestedRate')}</span>
                          <span className="font-mono">{formatCurrency(carryOver.suggested_loan_payment)}</span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={applyLoanDeduction}
                        className="w-full mt-2 text-blue-700 border-blue-300 hover:bg-blue-100"
                        data-testid="sidebar-apply-loan-btn"
                      >
                        {t('applyMonthlyRate')}
                      </Button>
                    </div>
                  )}
                  
                  {/* Info Text */}
                  <p className="text-xs text-gray-500 italic">
                    {t('autoDeductionNote')}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Preview */}
            {preview && (
              <Card className="bg-white border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-[#1A1A1A] text-lg flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-[#E31E24]" />
                    {t('preview')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  {/* Grundbrutto */}
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">{t('baseGross')}</span>
                    <span className="text-[#1A1A1A] font-mono">{formatCurrency(preview.baseGross)}</span>
                  </div>
                  
                  {/* Zuschläge Section */}
                  <div className="border-t border-gray-100 pt-2 mt-2">
                    <p className="text-xs font-semibold text-gray-500 mb-1">{t('surcharges')}</p>
                    
                    {parseFloat(preview.overtimePay) > 0 && (
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500 text-xs">{t('overtimeHours')} (+{preview.overtimePct}%)</span>
                        <span className="text-[#1A1A1A] font-mono text-xs">{formatCurrency(preview.overtimePay)}</span>
                      </div>
                    )}
                    
                    {parseFloat(preview.nightSurcharge) > 0 && (
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500 text-xs">{t('nightHours')} (+{preview.nightPct}%)</span>
                        <span className="text-[#1A1A1A] font-mono text-xs">{formatCurrency(preview.nightSurcharge)}</span>
                      </div>
                    )}
                    
                    {parseFloat(preview.holidaySurcharge) > 0 && (
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500 text-xs">{t('holidayHours')} (+{preview.holidayPct}%)</span>
                        <span className="text-[#1A1A1A] font-mono text-xs">{formatCurrency(preview.holidaySurcharge)}</span>
                      </div>
                    )}
                    
                    {parseFloat(preview.weekendSurcharge) > 0 && (
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500 text-xs">{t('weekendHours')} (+{preview.weekendPct}%)</span>
                        <span className="text-[#1A1A1A] font-mono text-xs">{formatCurrency(preview.weekendSurcharge)}</span>
                      </div>
                    )}
                    
                    {parseFloat(preview.otherSurcharges) > 0 && (
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500 text-xs">{t('otherSurcharge')} ({preview.otherPct}%)</span>
                        <span className="text-[#1A1A1A] font-mono text-xs">{formatCurrency(preview.otherSurcharges)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between py-1 mt-1 bg-gray-50 px-2 -mx-2 rounded">
                      <span className="text-gray-600 font-medium">{t('totalSurchargesLabel')}</span>
                      <span className="text-[#1A1A1A] font-mono font-medium">{formatCurrency(parseFloat(preview.totalSurcharges) + parseFloat(preview.overtimePay))}</span>
                    </div>
                  </div>
                  
                  {/* Urlaub */}
                  {parseFloat(preview.vacationPay) > 0 && (
                    <div className="border-t border-gray-100 pt-2 mt-2">
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500 text-xs">{t('vacationPay')} ({preview.vacationHours}h)</span>
                        <span className="text-[#1A1A1A] font-mono text-xs">{formatCurrency(preview.vacationPay)}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Krankheitstage */}
                  {parseFloat(preview.sickPay) > 0 && (
                    <div className="border-t border-gray-100 pt-2 mt-2">
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500 text-xs">{t('sickPay')} ({preview.sickHours}h = {preview.sickDays} {t('days')} × {preview.sickHoursPerDay}h)</span>
                        <span className="text-[#1A1A1A] font-mono text-xs">{formatCurrency(preview.sickPay)}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Bonus Section */}
                  {parseFloat(preview.totalBonus) > 0 && (
                    <div className="border-t border-gray-100 pt-2 mt-2">
                      <p className="text-xs font-semibold text-gray-500 mb-1">{t('bonusExtras')}</p>
                      
                      {parseFloat(preview.standardBonus) > 0 && (
                        <div className="flex justify-between py-0.5">
                          <span className="text-gray-500 text-xs">{t('standardBonus')}</span>
                          <span className="text-[#1A1A1A] font-mono text-xs">{formatCurrency(preview.standardBonus)}</span>
                        </div>
                      )}
                      
                      {parseFloat(preview.oneTimeBonus) > 0 && (
                        <div className="flex justify-between py-0.5">
                          <span className="text-gray-500 text-xs">{t('oneTimeBonus')}</span>
                          <span className="text-[#1A1A1A] font-mono text-xs">{formatCurrency(preview.oneTimeBonus)}</span>
                        </div>
                      )}
                      
                      {parseFloat(preview.commission) > 0 && (
                        <div className="flex justify-between py-0.5">
                          <span className="text-gray-500 text-xs">{t('commission')}</span>
                          <span className="text-[#1A1A1A] font-mono text-xs">{formatCurrency(preview.commission)}</span>
                        </div>
                      )}
                      
                      {parseFloat(preview.specialPayments) > 0 && (
                        <div className="flex justify-between py-0.5">
                          <span className="text-gray-500 text-xs">{t('specialPayments')}</span>
                          <span className="text-[#1A1A1A] font-mono text-xs">{formatCurrency(preview.specialPayments)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between py-1 mt-1 bg-blue-50 px-2 -mx-2 rounded">
                        <span className="text-gray-600 font-medium">{t('totalBonusLabel')}</span>
                        <span className="text-blue-600 font-mono font-medium">{formatCurrency(preview.totalBonus)}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Gesamt Brutto */}
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="flex justify-between py-1 bg-green-50 px-2 -mx-2 rounded">
                      <span className="text-gray-700 font-semibold">= {t('totalGrossLabel')}</span>
                      <span className="text-green-600 font-mono font-bold">{formatCurrency(preview.totalGross)}</span>
                    </div>
                  </div>
                  
                  {/* Auszahlung Brutto */}
                  <div className="border-t-2 border-[#E31E24] pt-3 mt-3">
                    <div className="flex justify-between py-2 bg-[#E31E24] px-3 -mx-2 rounded">
                      <span className="text-white font-bold">= AUSZAHLUNG BRUTTO</span>
                      <span className="text-white font-mono font-bold text-lg">{formatCurrency(preview.payoutGross)}</span>
                    </div>
                  </div>
                  
                  {/* Abzüge als Hinweis für Steuerberater */}
                  {parseFloat(preview.totalDeductions) > 0 && (
                    <div className="border-t border-orange-200 pt-3 mt-3 bg-orange-50 -mx-4 px-4 pb-3 rounded-b-lg">
                      <p className="text-xs font-semibold text-orange-700 mb-2 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        HINWEIS FÜR STEUERBERATER
                      </p>
                      <p className="text-xs text-orange-600 mb-2">
                        Folgende Abzüge sind vom NETTO-Gehalt abzuziehen:
                      </p>
                      
                      {parseFloat(preview.advanceDeduction) > 0 && (
                        <div className="flex justify-between py-0.5">
                          <span className="text-orange-600 text-xs">{t('advance')}</span>
                          <span className="text-orange-700 font-mono text-xs font-medium">{formatCurrency(preview.advanceDeduction)}</span>
                        </div>
                      )}
                      
                      {parseFloat(preview.loanDeduction) > 0 && (
                        <div className="flex justify-between py-0.5">
                          <span className="text-orange-600 text-xs">{t('loan')}</span>
                          <span className="text-orange-700 font-mono text-xs font-medium">{formatCurrency(preview.loanDeduction)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between py-1 mt-1 border-t border-orange-200">
                        <span className="text-orange-700 font-medium text-xs">Gesamt Abzüge vom Netto:</span>
                        <span className="text-orange-700 font-mono font-bold text-sm">{formatCurrency(preview.totalDeductions)}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Submit Button */}
            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                disabled={saving || !form.employee_id || !form.period_start || !form.period_end}
                className="w-full bg-[#E31E24] hover:bg-[#C41218] text-white btn-primary-glow"
                data-testid="save-button"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('loading')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    {isEdit ? t('saveChanges') : `${t('save')} (${t('draft')})`}
                  </span>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/payroll')}
                className="w-full border-gray-200 text-gray-500 hover:text-[#1A1A1A] hover:bg-gray-100"
                data-testid="cancel-button"
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
